import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useLocation, useNavigate } from 'react-router-dom';
import SignupStep from './steps/SignupStep';
import PricingStep from './steps/PricingStep';
import TrialPaymentStep from './steps/TrialPaymentStep';
import CompletionStep from './steps/CompletionStep';
import StepIndicator from './steps/StepIndicator';
import { useOnboardingDataRedux } from '../hooks/useOnboardingDataRedux';
import OnboardingDataDebug from './OnboardingDataDebug';

// Initialize Stripe with performance optimization - preload immediately
// Auto-select Stripe key based on environment
const stripeKey = process.env.NODE_ENV === 'production' 
  ?process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TESTING
  //  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_PROD 
  : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TESTING;

if (!stripeKey) {
  const keyName = process.env.NODE_ENV === 'production' 
    ? 'REACT_APP_STRIPE_PUBLISHABLE_KEY_TESTING' 
    : 'REACT_APP_STRIPE_PUBLISHABLE_KEY_TESTING';
  throw new Error(`${keyName} is required in environment variables`);
}
const isTestMode = stripeKey.startsWith('pk_test_');
if (isTestMode) {
  console.log('🧪 Frontend using Stripe test mode - no real charges will be made');
} else {
  console.log('💰 Frontend using Stripe live mode - real payments enabled');
}
const stripePromise = loadStripe(stripeKey);

interface MultiStepSignupScreenProps {
  startStep?: number;
}

const MultiStepSignupScreen: React.FC<MultiStepSignupScreenProps> = ({ 
  startStep = 1 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    data: onboardingData,
    isLoaded,
    updateData,
    nextStep,
    goToStep,
    resetData,
    getCompletionPercentage,
    completeOnboarding
  } = useOnboardingDataRedux();

  // Use Redux state directly - no more local state
  const currentStep = onboardingData.currentStep;
  const completedSteps = onboardingData.completedSteps;
  const selectedPlan = onboardingData.selectedPlan;
  const userEmail = onboardingData.email;
  const userPhoneNumber = onboardingData.phoneNumber;

  // Clean up onboarding data when component unmounts if all steps are completed
  // DISABLED: This might be interfering with data persistence during error recovery
  // useEffect(() => {
  //   return () => {
  //     if (completedSteps.every(step => step)) {
  //       resetData();
  //     }
  //   };
  // }, [completedSteps, resetData]);

  // Create setter functions that dispatch Redux actions for child component compatibility
  const setCurrentStep = (step: number) => goToStep(step);
  const setCompletedSteps = (steps: boolean[]) => {
    // Redux manages completed steps automatically, but we can update specific steps if needed
    const updates: any = { completedSteps: steps };
    updateData(updates);
  };
  const setSelectedPlan = (plan: string) => updateData({ selectedPlan: plan });
  const setUserEmail = (email: string) => updateData({ email });
  const setUserPhoneNumber = (phoneNumber: string) => updateData({ phoneNumber });

  // Redux handles state initialization automatically via persistence

  // Read onboarding step from query param and update persistent data
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const onboardingStep = params.get('onboarding');
    if (onboardingStep && isLoaded) {
      const stepNumber = Number(onboardingStep);
      goToStep(stepNumber);
      // Optionally, clean up the URL
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, location.pathname, isLoaded, goToStep]);

  // Update current step when startStep changes (only if not already set from persistent data)
  useEffect(() => {
    if (startStep !== currentStep && (currentStep === 1 || startStep > currentStep) && isLoaded) {
      // Only override if we don't have persistent data indicating we're further along
      if (onboardingData.currentStep <= startStep) {
        goToStep(startStep);
      }
    }
  }, [startStep, currentStep, isLoaded, goToStep, onboardingData.currentStep]);

  const handleClose = () => {
    // Always allow closing and navigate back to landing page
    resetData(); // Redux will reset all data including step and form data
    navigate('/'); // Navigate back to landing page
  };

  return (
    <Elements stripe={stripePromise}>
      <div className="fixed inset-0 bg-white overflow-y-auto"
           style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
        <div className="flex items-center justify-center min-h-screen p-4">
        
        {/* Subtle animated background elements - minimal and professional */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-gray-300 rounded-full animate-pulse opacity-40"></div>
          <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-gray-400 rounded-full animate-pulse delay-1000 opacity-30"></div>
          <div className="absolute top-1/2 left-1/6 w-1.5 h-1.5 bg-gray-200 rounded-full animate-pulse delay-2000 opacity-50"></div>
          <div className="absolute bottom-1/4 right-1/6 w-2 h-2 bg-gray-300 rounded-full animate-pulse delay-3000 opacity-40"></div>
        </div>
        
        {/* Clean modal container */}
        <div className="relative w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-3xl xl:max-w-4xl 
                        bg-white rounded-xl md:rounded-2xl shadow-xl border border-gray-100
                        my-4 md:my-8 min-h-fit">
          
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute right-3 top-3 md:right-4 md:top-4 z-10 
                       p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 
                       rounded-full transition-all duration-300 group"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header Section */}
          <div className="px-4 py-6 md:px-6 md:py-8 border-b border-gray-100">
            {/* Logo and Title */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <img 
                  src="/images/noBgColor.png" 
                  alt="Silas Logo" 
                  className="h-10 w-auto object-contain transition-transform duration-300 hover:scale-105"
                />
              </div>
              {/* <h1 className="text-xl md:text-2xl font-bold text-black mb-2" style={{ fontFamily: "'Canela', serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
                Join Silas
              </h1> */}
              <p className="text-sm md:text-base text-gray-600">Your mental health journey begins here</p>
            </div>
            <StepIndicator currentStep={currentStep} completedSteps={completedSteps} />
          </div>
          
          {/* Content Section */}
          <div className="px-4 py-6 md:px-6 md:py-8">
            <SignupStep
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              completedSteps={completedSteps}
              setCompletedSteps={setCompletedSteps}
              onClose={handleClose}
              userEmail={userEmail}
              setUserEmail={setUserEmail}
            />
            <PricingStep
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              completedSteps={completedSteps}
              setCompletedSteps={setCompletedSteps}
              selectedPlan={selectedPlan}
              setSelectedPlan={setSelectedPlan}
              onClose={handleClose}
            />
            <CompletionStep
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              completedSteps={completedSteps}
              setCompletedSteps={setCompletedSteps}
              selectedPlan={selectedPlan}
              onClose={handleClose}
              userEmail={userEmail}
              userPhoneNumber={userPhoneNumber}
              setUserPhoneNumber={setUserPhoneNumber}
            />
            <TrialPaymentStep
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              completedSteps={completedSteps}
              setCompletedSteps={setCompletedSteps}
              selectedPlan={selectedPlan}
              onClose={handleClose}
            />
          </div>
        </div>
        </div>
        
        {/* Debug Component - Only shows in development */}
        <OnboardingDataDebug />
      </div>
    </Elements>
  );
};

export default MultiStepSignupScreen;
